import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { selectVisibleGridSheets } from 'store/schedule/scheduleSelectors';
import { getEventSheetsFuse } from 'pages/ScheduleV6/utils';
import { globalBEDateFormat } from 'common/dateFormatConfig';
import { format } from 'date-fns';

const COLUMNS_COUNT = 5;
const MIN_ROWS_COUNT = 6;

/**
 * Maps adapted schedule sheets into a simple grid structure for GridView.
 *
 * For now we:
 * - use entry.grid.position (if present) as a linear index;
 * - compute rows/cols so that all positions fit into a 5-column grid;
 * - expose a map of { position -> entry }.
 *
 * Drag-and-drop and conflict logic can be layered on top of this structure.
 */
export const useV6GridEvents = (selectedDate) => {
  const sheets = useSelector(selectVisibleGridSheets);
  const { searchValue, showAllLocations } = useSelector((store) => store.schedule);
  const locations = useSelector((state) => state.personalProfile.activeLocations);

  const gridSheets = useMemo(() => {
    return sheets.filter(sheet => (selectedDate
      ? sheet.grid?.date === format(new Date(selectedDate), globalBEDateFormat)
      : true
    ))
  }, [selectedDate, sheets])
  
  const visibleSheets = useMemo(() => {
    const fuse = getEventSheetsFuse(gridSheets);
    if (searchValue) return fuse.search(searchValue);
    return gridSheets;
  }, [gridSheets, searchValue])

  const { gridCells, rowsCount, colsCount } = useMemo(() => {
    if (!Array.isArray(visibleSheets) || !visibleSheets.length) {
      return {
        gridCells: {},
        rowsCount: MIN_ROWS_COUNT,
        colsCount: COLUMNS_COUNT,
      };
    }

    const gridCells = {};
    let maxPosition = 0;
    const getPosition = (entry) => entry.grid?.position ?? 0;
    const sortedSheets = [...visibleSheets].sort(
      (a, b) => getPosition(a) - getPosition(b) || a._id.localeCompare(b._id)
    );

    structuredClone(sortedSheets).forEach((entry) => {
      const locationColor = showAllLocations
        ? locations.find(({ _id }) => entry?.sheet?.tenantLocationId === _id)?.color
        : null;
      entry.locationColor = locationColor;

      // Colliding positions (other locations, concurrent creates) shift to the next free cell.
      let pos = getPosition(entry);
      while (gridCells[pos]) pos += 1;

      maxPosition = Math.max(maxPosition, pos);
      gridCells[pos] = entry;
    });

    const totalSlots = maxPosition + 1;
    const computedRows = Math.ceil(totalSlots / COLUMNS_COUNT);
    const rowsCount = Math.max(MIN_ROWS_COUNT, computedRows || MIN_ROWS_COUNT);

    return {
      gridCells,
      rowsCount,
      colsCount: COLUMNS_COUNT,
    };
  }, [visibleSheets, showAllLocations, locations]);

  return { gridCells, rowsCount, colsCount };
};

export default useV6GridEvents;

